import React, { useCallback, useMemo } from "react";
import MapboxDraw, { MapboxDrawOptions } from "@mapbox/mapbox-gl-draw";

import MultiLayerEditPanelContent from "fond/draw/MultiLayerEdit/MultiLayerEditPanelContent";
import { MultiLayerEditProvider } from "fond/draw/MultiLayerEdit/MultiLayerEditProvider";
import { validateTransformerHub } from "fond/draw/validation/muPowerValidation";
import {
  DeleteButton,
  PointButton,
  PolygonSelectButton,
  RedoButton,
  SelectButton,
  SnappingButton,
  ToolbarConfig,
  UndoButton,
} from "fond/map/Toolbar";
import { MuPowerEditableLayerIds, MuPowerSolverLayerIds } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";
import { useStackedNavigationContext } from "fond/widgets";

const toolbarConfig: ToolbarConfig = [
  {
    id: "mainActions",
    type: "mode",
    items: [
      <SelectButton key="select" />,
      <PolygonSelectButton key="selectPolygon" />,
      <PolygonSelectButton key="selectLasso" selectionMode="lasso" />,
    ],
  },
  {
    id: "drawTools",
    type: "mode",
    items: [<PointButton key="drawPoint" />],
  },
  {
    id: "miscTools",
    type: "action",
    items: [<SnappingButton key="snapping" />],
  },
  {
    id: "historyTools",
    type: "action",
    items: [<UndoButton key="undo" />, <RedoButton key="redo" />, <DeleteButton key="delete" />],
  },
];

const MuPowerTransformersDraw: React.FC = () => {
  const versionId = useAppSelector((state) => state.project.versionId);
  const { clear } = useStackedNavigationContext();

  const snapGetFeatures = useCallback((m: mapboxgl.Map, draw: MapboxDraw) => {
    return draw.getAll().features.filter((f) => f.properties?.layerId === MuPowerEditableLayerIds.ugPoints);
  }, []);

  const options: MapboxDrawOptions = useMemo(
    () => ({
      snap: true,
      snapOptions: {
        snapPx: 25,
        snapToMidPoints: false,
        snapVertexPriorityDistance: 0.000025,
        snapGetFeatures,
      },
      editOptions: {
        allLayers: [
          {
            ID: MuPowerSolverLayerIds.transformerHubs,
            GeometryType: "Point",
            getFeatureProperties: () => ({ layerId: MuPowerSolverLayerIds.transformerHubs }),
            validate: validateTransformerHub,
          },
          {
            ID: MuPowerSolverLayerIds.sourceHubs,
            GeometryType: "Point",
            Hide: true,
          },
          {
            ID: MuPowerEditableLayerIds.ugPoints,
            GeometryType: "Point",
            Hide: true,
          },
        ],
        editingLayerId: MuPowerSolverLayerIds.transformerHubs,
        editingLayerIds: [MuPowerSolverLayerIds.transformerHubs],
      },
    }),
    [snapGetFeatures]
  );

  return (
    <MultiLayerEditProvider versionId={versionId} options={options} config={toolbarConfig}>
      <MultiLayerEditPanelContent title="Edit transformers" alertTitle="Edit transformers" onFinish={clear} />
    </MultiLayerEditProvider>
  );
};

export default MuPowerTransformersDraw;
